"use client";

import React from "react";
import { BlurFade } from "@/components/ui/blur-fade";
import { Card } from "@/components/ui/card";
import { CheckCircle } from "lucide-react";
import Image from "next/image";

interface FeatureShowcaseProps {
  title: string;
  description: string;
  bulletPoints?: string[];
  image?: string;
  badge?: string;
}

const FeatureShowcase = ({ title, description, bulletPoints, image, badge }: FeatureShowcaseProps) => {
  return (
    <section className="py-24 px-6 bg-secondary">
      <div className="container max-w-6xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left column - Text content */}
          <div className="space-y-6">
            {badge && (
              <BlurFade delay={0.05}>
                <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 border border-primary/20">
                  <span className="text-sm font-medium text-primary">{badge}</span>
                </div>
              </BlurFade>
            )}

            <BlurFade delay={0.1}>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-foreground leading-[1.1]">
                {title}
              </h2>
            </BlurFade>

            <BlurFade delay={0.15}>
              <p className="text-lg text-foreground/60 leading-relaxed">
                {description}
              </p>
            </BlurFade>

            {bulletPoints && bulletPoints.length > 0 && (
              <BlurFade delay={0.2}>
                <ul className="space-y-3 pt-2">
                  {bulletPoints.map((point, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckCircle
                        size={20}
                        className="text-primary flex-shrink-0 mt-0.5"
                        strokeWidth={2}
                      />
                      <span className="text-foreground/70 leading-relaxed">{point}</span>
                    </li>
                  ))}
                </ul>
              </BlurFade>
            )}
          </div>

          {/* Right column - Image / Placeholder */}
          <BlurFade delay={0.2}>
            {image ? (
              <div className="relative w-full aspect-[4/3] rounded-3xl overflow-hidden shadow-2xl">
                <Image
                  src={image}
                  alt={title}
                  fill
                  className="object-cover"
                />
              </div>
            ) : (
              <Card className="w-full aspect-[4/3] rounded-3xl bg-white/60 border-0 flex items-center justify-center overflow-hidden relative">
                <div className="absolute inset-0 bg-gradient-to-br from-white/80 via-white/40 to-primary/5" />
                <div className="relative z-10 flex flex-col items-center gap-4 text-foreground/20">
                  <div className="w-20 h-20 rounded-2xl border-2 border-dashed border-foreground/10 flex items-center justify-center">
                    <div className="w-10 h-10 rounded-xl bg-primary/10" />
                  </div>
                  <div className="space-y-2 text-center">
                    <div className="w-32 h-2 bg-foreground/10 rounded-full" />
                    <div className="w-24 h-2 bg-foreground/5 rounded-full mx-auto" />
                  </div>
                </div>
              </Card>
            )}
          </BlurFade>
        </div>
      </div>
    </section>
  );
};

export default FeatureShowcase;
